import AsyncStorage from '@react-native-async-storage/async-storage';
import aes from 'crypto-js/aes'
import encUtf8 from "crypto-js/enc-utf8";
import { encryptCache } from 'utils/cache/encrypt-cache'
import HelperDevelopment from "utils/helper-development";
import HelperArray from 'utils/helper-array'
import HelperString from 'utils/helper-string'
import _ from "lodash";
import { storeKeys } from 'services/store/keys'
import UserService from 'services/user'
import store from 'services/redux/store'
import { resetData } from 'services/redux/data/actions'
import { resetManagement } from 'services/redux/management/actions'
import { resetAuthentication } from 'services/redux/authentication/actions'
import { resetCart } from 'services/redux/cart/actions'
import SecureStoreService from 'services/store/secure-store'
import { appRestart } from 'utils/helper-restart'
import HelperDate, { dateFormat } from 'utils/helper-date'

const AccountStorage = {
	
	key: 'CAlMOTEONickRANdUchYSiOCtOdecTIcTiNglOmENteRthYSIoCHmaCOrAInTHoUTRAPERoTEPcOLiSCHoRTHASOlPaLKwIsaPr',
	encrypt: !HelperDevelopment.isTestWeb(),
	cachePrefix: '@accounts_',
	debug: [],
	errors: [],
	
	createKey: ( key ) => {
		return `${AccountStorage.cachePrefix}${ key }`
	},
	
	createNewIdentifier: async () => {
		let identifier = HelperString.random( 4 );
		
		await AccountStorage.getData( 'identifiers', 'identifiers' ).then( ( result ) => {
			let newIdentifiers = [];
			
			if( result !== null && _.isArray( result.data ) ){
				newIdentifiers = result.data;
				
				while( newIdentifiers.indexOf( identifier ) !== -1 ){
					identifier = HelperString.random( 4 );
				}
			}
			
			newIdentifiers.push( identifier );
			
			return AccountStorage.storeData( 'identifiers', 'identifiers', newIdentifiers );
		});
		
		return identifier;
	},
	
	storeAccount: async ( identifier, data ) => {
		await AccountStorage.storeData( identifier, `account_${identifier}`, data );
		const index = await AccountStorage.getData( 'identifiers', 'identifiers' );
		const identifiers = _.isArray( index?.data ) ? index.data : [];
		if( !identifiers.includes( identifier ) ) {
			await AccountStorage.storeData( 'identifiers', 'identifiers', [ ...identifiers, identifier ] );
		}
	},

	saveCurrentAccount: async ( { employeeName, companyName } = {} ) => {
		const data = await SecureStoreService.getMulti([
			storeKeys.ACCOUNT_IDENTIFIER, storeKeys.DOMAIN, storeKeys.DOMAIN_PREFIX,
			storeKeys.EMPLOYEE_GUID, storeKeys.EMPLOYEE_SESSION, storeKeys.ENCRYPTION_KEY,
			storeKeys.EMPLOYEE_NAME, storeKeys.COMPANY_NAME,
		]);
		if( !data?.[ storeKeys.EMPLOYEE_GUID ] || !data?.[ storeKeys.EMPLOYEE_SESSION ] || !data?.[ storeKeys.DOMAIN ] || !data?.[ storeKeys.ENCRYPTION_KEY ] ) {
			throw new Error( 'Cannot save an account without a complete login session' );
		}
		const identifier = data[ storeKeys.ACCOUNT_IDENTIFIER ] || await AccountStorage.createNewIdentifier();
		const account = {
			...data,
			[ storeKeys.ACCOUNT_IDENTIFIER ]: identifier,
			[ storeKeys.EMPLOYEE_NAME ]: employeeName ?? data[ storeKeys.EMPLOYEE_NAME ],
			[ storeKeys.COMPANY_NAME ]: companyName ?? data[ storeKeys.COMPANY_NAME ],
		};
		await AccountStorage.storeAccount( identifier, account );
		await SecureStoreService.set( storeKeys.ACCOUNT_IDENTIFIER, identifier );
		return account;
	},
	
	getAccounts: async () => {
		let accounts = {};
		
		await AccountStorage.getData( 'identifiers', 'identifiers' )
            .then( async ( result ) => {
				AccountStorage.debug.push( JSON.stringify( result ) )
	   
				if ( result !== null && _.isArray( result.data ) ) {
					for( const identifier of result.data ) {
						const account = await AccountStorage.getData( identifier, `account_${identifier}`);
						AccountStorage.debug.push( JSON.stringify( account ) )
						
						if ( account !== null ) {
							accounts[ identifier ] = account.data;
						}
					}
				}
			})
			.catch( ( e ) => {
				AccountStorage.errors.push( e )
			});
		
		return accounts;
	},
	
	hasMultipleAccounts: async () => {
		let accounts = await AccountStorage.getAccounts();
        
        return _.size( accounts ) > 1;
    },
	
	removeAccount: async ( identifier ) => {
        await AccountStorage.removeData( `account_${identifier}` );
		
		await AccountStorage.getData( 'identifiers', 'identifiers' )
            .then( async ( result ) => {
	            if( result !== null &&  _.isArray( result.data ) ){
		
		            let newIdentifiers = _.filter( result.data, ( newIdentifier ) => {
	                    return newIdentifier !== identifier;
	                });
		            
	                await AccountStorage.storeData( 'identifiers', 'identifiers', newIdentifiers );
	            }
	        })
			.catch( ( e ) => {
				AccountStorage.errors.push( e )
			});
    },
	
	storeData: async ( seed, key, data ) => {
		try {
			let storageData = JSON.stringify({
				updated_at: HelperDate.date().format( dateFormat.DATE ),
				touched_at: HelperDate.date().format( dateFormat.DATE ),
				data: data
			});
			
			if( AccountStorage.encrypt ) {
				storageData = AccountStorage.encode( seed, storageData );
			}
			
			await AsyncStorage.setItem( AccountStorage.createKey( key ), storageData )
		} catch (e) {
			// saving error
			AccountStorage.errors.push( e )
			throw e;
		}
	},
	
	getData: async ( seed, key ) => {
		try {
			let jsonValue = await AsyncStorage.getItem( AccountStorage.createKey( key ) );
			if( jsonValue === null ) {
				return null;
			}
			
			if( AccountStorage.encrypt ) {
				jsonValue = AccountStorage.decode( seed, jsonValue );
			}
			
			return jsonValue != null && jsonValue.length > 0 ? JSON.parse(jsonValue) : null;
		} catch(e) {
			AccountStorage.errors.push( e )
		}
		
		return null;
	},
	
	removeData: async ( key ) => {
		await AsyncStorage.removeItem( AccountStorage.createKey( key ) );
	},
	
	clearAccounts: async ( ) => {
		const keys = await AccountStorage.getCacheKeys();
		
		await AsyncStorage.multiRemove( keys );
	},
	
	getCacheKeys: async () => {
		let keys = await AsyncStorage.getAllKeys();
		
		let return_keys = [];
		
		keys.forEach( ( key ) => {
			if( _.includes( key, AccountStorage.cachePrefix ) ) {
				return_keys.push( key.toString() );
			}
		})
		
		return return_keys;
	},
	createEncryptionKey: ( seed ) => {
		return HelperArray.shuffleWithSeed( AccountStorage.key.split(""), seed ).join("");
	},
	
	encode: ( seed, data ) => {
		return encryptCache( data, AccountStorage.createEncryptionKey( seed ) );
	},
	
	decode: ( seed, data ) => {
		const bytes  = aes.decrypt( data ,  AccountStorage.createEncryptionKey( seed ) );
		return bytes.toString( encUtf8 );
	},
	
	writeToConsole: async () => {
		let keys = await AccountStorage.getCacheKeys();
		
		if( keys.length > 0 ) {
			keys.forEach( ( key ) => {
				AccountStorage.getData( key.replace( AccountStorage.cachePrefix, '' ).replace( 'account_', ''), key.replace( AccountStorage.cachePrefix, '' ) ).then( ( results ) => {
					console.prefix( `this::writeToConsole ${key}`, {...results} );
				} );
			} );
		}else{
			console.prefix( `this::writeToConsole`, 'Cache is empty' );
		}
	},
	
	switch: ( account ) => {
		let secureData = {
			[ storeKeys.ACCOUNT_IDENTIFIER ]: account[ storeKeys.ACCOUNT_IDENTIFIER ],
			[ storeKeys.DOMAIN ]: account[ storeKeys.DOMAIN ],
			[ storeKeys.COMPANY_NAME ]: account[ storeKeys.COMPANY_NAME ],
			[ storeKeys.EMPLOYEE_GUID ]: account[ storeKeys.EMPLOYEE_GUID ],
			[ storeKeys.EMPLOYEE_NAME ]: account[ storeKeys.EMPLOYEE_NAME ],
			[ storeKeys.EMPLOYEE_SESSION ]: account[ storeKeys.EMPLOYEE_SESSION ],
			[ storeKeys.ENCRYPTION_KEY ]: account[ storeKeys.ENCRYPTION_KEY ]
		};
		
		if ( _.get( account, storeKeys.DOMAIN_PREFIX, '' ) !== '' ) {
			secureData[ storeKeys.DOMAIN_PREFIX ] = _.get( account, storeKeys.DOMAIN_PREFIX, '' );
		}
		
		return UserService.logout({
			// Restore the selected session before restarting, including in Expo Go.
			restart: false,
			resetData: () => {
				return store.dispatch(resetData());
			},
			resetManagement: () => {
				return store.dispatch(resetManagement());
			},
			resetAuthentication: () => {
				return store.dispatch(resetAuthentication());
			},
			resetCart: () => {
				return store.dispatch(resetCart());
			},
			callback: () => {
				return SecureStoreService.setMulti( secureData ).then( () => {
					return appRestart();
				} );
			}
		});
	},
	
	getErrors: () => {
		return _.map( AccountStorage.errors, error => {
			if( typeof error !== 'undefined' ) {
				return error.toString()
			}
			
			return null;
		} )
	},
}

export default AccountStorage
